import React from 'react';
import { motion } from 'framer-motion';

const CONSULTATION_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/ineonchj_consultation.webp';

const groupSteps = [
  { n: 1, title: 'Join the membership' },
  { n: 2, title: 'Access the live schedule and community' },
  { n: 3, title: 'Train with the group' },
];

const coachingSteps = [
  { n: 1, title: 'Submit a coaching inquiry' },
  { n: 2, title: 'Book a 15-minute consultation' },
  { n: 3, title: 'Choose the right coaching program' },
  { n: 4, title: 'Complete enrollment and begin' },
];

const Placeholder = () => (
  <div className="relative w-full h-full min-h-[320px] overflow-hidden">
    <img
      src={CONSULTATION_IMG}
      alt="Consultation with Coach Alou"
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
  </div>
);

const StepDot = ({ n }) => (
  <div className="w-10 h-10 rounded-full border-2 border-gray-500 text-white font-display flex items-center justify-center text-base shrink-0">
    {n}
  </div>
);

const Flow = ({ title, steps }) => (
  <div>
    <p className="font-display text-gray-300 uppercase text-xs tracking-[0.35em] mb-5">{title}</p>
    <div className="relative">
      {/* horizontal connector line - desktop only */}
      <div className="absolute left-5 right-5 top-5 h-px bg-neutral-800 hidden sm:block" />
      <div
        className="grid gap-6 sm:gap-4 grid-cols-1"
        style={{ gridTemplateColumns: `repeat(1, minmax(0, 1fr))` }}
      >
        <div
          className="grid gap-4 sm:gap-3"
          style={{
            gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`,
            display: 'grid',
          }}
        >
          {steps.map((s) => (
            <div key={s.n} className="relative flex flex-col items-start gap-3">
              <StepDot n={s.n} />
              <p className="text-gray-300 text-xs sm:text-sm leading-snug">{s.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const TheProcess = () => {
  return (
    <section
      id="process"
      data-testid="the-process"
      className="bg-black py-14 sm:py-16 lg:py-20 px-6 border-b border-neutral-900"
    >
      <div className="container mx-auto max-w-7xl">
        <div className="grid 2xl:grid-cols-[1fr_1.5fr_1fr] lg:grid-cols-[1fr_2fr] gap-8 lg:gap-10 items-start">
          {/* Left title block */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-baseline gap-4 mb-2">
              <span className="font-display text-gray-500 text-2xl">3.</span>
              <p className="text-gray-400 font-bold text-xs tracking-[0.35em] uppercase">
                How To Get Started
              </p>
            </div>
            <p className="text-gray-400 uppercase text-xs tracking-[0.35em] mt-3">The Process</p>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-[42px] xl:text-5xl uppercase leading-[0.95] mt-3">
              A Clear Path <br />For Every Level <br />Of Support.
            </h2>
          </motion.div>

          {/* Middle flows */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="space-y-10"
          >
            <Flow title="Group Fitness" steps={groupSteps} />
            <div className="h-px bg-neutral-800" />
            <Flow title="Guided + Private Coaching" steps={coachingSteps} />
            <p className="text-gray-500 text-xs italic">
              The consultation is a short fit conversation—not a free coaching session.
            </p>
          </motion.div>

          {/* Right image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="hidden 2xl:block relative rounded-2xl overflow-hidden border border-neutral-800 h-full"
          >
            <Placeholder />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
